export interface IRoundQuantityPickerStateProps {
  stepSize?: number;
  minValue?: number;
  maxValue?: number;
  value: number;
  onChange: (newValue: number) => void;
}

type useRoundQuantityPickerStateFunction<T> = (
  props: T
) => [value: number, dispatcher: (action: "increment" | "decrement") => void];

export const useRoundQuantityPickerState: useRoundQuantityPickerStateFunction<
  IRoundQuantityPickerStateProps
> = ({ onChange, value, minValue, maxValue, stepSize }) => {
  const nearestStep = (isIncreament: boolean) => {
    if (!stepSize) {
      return value + (isIncreament ? 1 : -1);
    }
    return (
      stepSize *
      (isIncreament
        ? Math.ceil(value / stepSize)
        : Math.floor(value / stepSize))
    );
  };

  const handleIncrement = () => {
    if (value === 0 && minValue !== undefined && minValue !== 0) {
      onChange(minValue);
    } else if (
      stepSize &&
      maxValue !== undefined &&
      value + stepSize >= maxValue
    ) {
      onChange(maxValue);
    } else if (maxValue === undefined || value < maxValue) {
      if (!stepSize) {
        onChange(value + 1);
      } else {
        onChange(value % stepSize === 0 ? value + stepSize : nearestStep(true));
      }
    }
  };

  const handleDecrement = () => {
    if (value === 0 || value === minValue) {
      onChange(0);
    } else if (maxValue !== undefined && value > maxValue) {
      onChange(maxValue);
    } else if (
      stepSize &&
      minValue !== undefined &&
      value - stepSize <= minValue
    ) {
      onChange(minValue);
    } else if (minValue === undefined || value > minValue) {
      if (!stepSize) {
        onChange(value - 1);
      } else {
        onChange(
          value % stepSize === 0 ? value - stepSize : nearestStep(false)
        );
      }
    }
  };

  const dispatcher = (action: "increment" | "decrement") => {
    switch (action) {
      case "increment":
        handleIncrement();
        break;
      case "decrement":
        handleDecrement();
        break;
      default:
        break;
    }
  };

  return [value, dispatcher];
};
